import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Search, Loader2, X, ChevronRight, Gavel, Highlighter, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePaywall } from "@/context/PaywallContext";

const selectCls =
  "bg-[#FDFBF7] border border-[#D9D4CC] px-3 py-2.5 text-sm text-[#2C2A29] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300";

const HL_COLORS = [
  { key: "yellow", bg: "#FCEFA1" },
  { key: "green", bg: "#CDE9C6" },
  { key: "blue", bg: "#BFD9F2" },
  { key: "pink", bg: "#F5C6D6" },
];
const HL_MAP = Object.fromEntries(HL_COLORS.map((c) => [c.key, c.bg]));
const LAST_KEY = "bible:last";

export default function Bible() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { openPaywall } = usePaywall();
  const [translations, setTranslations] = useState([]);
  const [books, setBooks] = useState([]);
  const hasToken = !!localStorage.getItem("iie_token");
  const savedLast = (() => { if (!hasToken) return null; try { return JSON.parse(localStorage.getItem(LAST_KEY) || "null"); } catch (_) { return null; } })();
  const [tr, setTr] = useState(savedLast?.tr || "KJV");
  const [testament, setTestament] = useState("all");
  const [book, setBook] = useState(savedLast?.book || 43); // John
  const [chapter, setChapter] = useState(savedLast?.chapter || 1);
  const [verses, setVerses] = useState(null);
  const [bookName, setBookName] = useState("");
  const [targetVerse, setTargetVerse] = useState(null);
  const [highlights, setHighlights] = useState({});
  const [openHl, setOpenHl] = useState(null);
  const reqRef = useRef(0);

  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null); // null=browse, else {mode, results}

  useEffect(() => {
    api.get("/bible/translations").then((r) => setTranslations(r.data)).catch(() => {});
    api.get("/bible/books").then((r) => setBooks(r.data)).catch(() => {});
  }, []);

  // Persist last-read position (signed-in convenience; guests don't resume)
  useEffect(() => {
    if (localStorage.getItem("iie_token")) {
      localStorage.setItem(LAST_KEY, JSON.stringify({ tr, book, chapter }));
    }
  }, [tr, book, chapter]);

  // Load highlights (signed-in only)
  useEffect(() => {
    if (user) {
      api.get("/bible/highlights").then((r) => setHighlights(r.data.highlights || {})).catch(() => {});
    } else {
      setHighlights({});
    }
  }, [user]);

  const applyHighlight = async (verse, color) => {
    if (!user) return;
    if (color && color !== "yellow" && user.tier !== "premium") {
      setOpenHl(null);
      openPaywall("Free accounts highlight in one color. Go Premium to unlock the full 4-color set.");
      return;
    }
    const ref = `${book}:${chapter}:${verse}`;
    const next = { ...highlights };
    if (color) next[ref] = color; else delete next[ref];
    setHighlights(next);
    setOpenHl(null);
    api.put("/bible/highlights", { ref, color }).catch(() => {});
  };

  useEffect(() => {
    const g = location.state?.goto;
    if (g?.book) {
      setResults(null);
      setBook(g.book);
      setChapter(g.chapter || 1);
      if (g.verse) setTargetVerse(g.verse);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const currentBook = useMemo(() => books.find((b) => b.num === book), [books, book]);
  const filteredBooks = useMemo(
    () => books.filter((b) => testament === "all" || b.testament === testament),
    [books, testament]
  );

  useEffect(() => {
    if (results) return; // don't fetch chapter while showing search
    const reqId = ++reqRef.current;
    api
      .get("/bible/chapter", { params: { tr, book, chapter } })
      .then((r) => {
        if (reqId !== reqRef.current) return; // ignore stale response
        setVerses(r.data.verses);
        setBookName(r.data.book_name);
      })
      .catch(() => { if (reqId === reqRef.current) setVerses([]); });
  }, [tr, book, chapter, results]);

  useEffect(() => {
    if (!verses || !targetVerse) return;
    let tries = 0;
    const id = setInterval(() => {
      const el = document.querySelector(`[data-testid="verse-${targetVerse}"]`);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - window.innerHeight / 2;
        window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
        clearInterval(id);
        setTargetVerse(null);
      } else if (++tries > 20) {
        clearInterval(id);
        setTargetVerse(null);
      }
    }, 50);
    return () => clearInterval(id);
  }, [verses, targetVerse]);

  const changeTestament = (t) => {
    setTestament(t);
    const list = books.filter((b) => t === "all" || b.testament === t);
    if (list.length && !list.find((b) => b.num === book)) {
      setBook(list[0].num);
      setChapter(1);
    }
  };

  const changeBook = (num) => { setBook(Number(num)); setChapter(1); };

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const params = { q: query.trim(), tr };
      if (testament !== "all") params.testament = testament;
      const { data } = await api.get("/bible/search", { params });
      setResults(data);
    } catch (_) {
      setResults({ mode: "keyword", results: [] });
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => { setResults(null); setQuery(""); };

  const openVerse = (r) => {
    setBook(r.book);
    setChapter(r.chapter);
    setTargetVerse(r.verse);
    setResults(null);
    setQuery("");
  };

  const decreeVerse = (reference, text) => {
    navigate("/generate", { state: { seedVerse: { reference: `${reference} (${tr})`, text } } });
  };

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Holy Bible</p>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">Read the Word</h1>
      <p className="font-decree text-lg text-[#7A756E] mt-3">
        Search a verse (e.g. “John 3:16”) or a phrase, or browse by book and chapter — in {translations.length || "many"} translations.
      </p>

      {/* Search */}
      <form onSubmit={doSearch} className="mt-8 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A39C90]" />
          <input
            data-testid="bible-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. John 3:16, Psalm 23, or “living water”"
            className="w-full bg-transparent border border-[#D9D4CC] pl-10 pr-9 py-3 text-base text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300"
          />
          {query && (
            <button type="button" onClick={clearSearch} aria-label="Clear" data-testid="bible-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#A39C90] hover:text-[#2C2A29] p-1">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button
          data-testid="bible-search-button"
          type="submit"
          className="px-5 py-3 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 text-sm"
        >
          Search
        </button>
      </form>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <select data-testid="bible-translation-select" value={tr} onChange={(e) => setTr(e.target.value)} className={selectCls}>
          {translations.map((t) => (
            <option key={t.code} value={t.code} disabled={!user && !t.guest_allowed}>
              {t.name} ({t.code}){!user && !t.guest_allowed ? " — sign in to unlock" : ""}
            </option>
          ))}
        </select>

        <div className="flex border border-[#D9D4CC] rounded-sm overflow-hidden">
          {[["all", "All"], ["OT", "Old"], ["NT", "New"]].map(([val, lbl]) => (
            <button
              key={val}
              data-testid={`bible-testament-${val}`}
              onClick={() => changeTestament(val)}
              className={`px-3.5 py-2.5 text-sm transition-colors duration-300 ${testament === val ? "bg-[#2C2A29] text-[#FAF9F6]" : "text-[#4A4744] hover:bg-[#EAE6DF]"}`}
            >
              {lbl}
            </button>
          ))}
        </div>

        <select data-testid="bible-book-select" value={book} onChange={(e) => changeBook(e.target.value)} className={selectCls}>
          {filteredBooks.map((b) => (
            <option key={b.num} value={b.num}>{b.name}</option>
          ))}
        </select>

        <select data-testid="bible-chapter-select" value={chapter} onChange={(e) => setChapter(Number(e.target.value))} className={selectCls}>
          {Array.from({ length: currentBook?.chapters || 1 }, (_, i) => i + 1).map((c) => (
            <option key={c} value={c}>Chapter {c}</option>
          ))}
        </select>
      </div>

      {/* Search results */}
      {results ? (
        <div className="mt-10" data-testid="bible-search-results">
          <div className="flex items-center justify-between mb-5">
            <p className="font-body text-sm uppercase tracking-[0.15em] text-[#7A756E]">
              {results.mode === "reference" ? "Verse" : `${results.results.length} result${results.results.length === 1 ? "" : "s"}`}
            </p>
            <button onClick={clearSearch} data-testid="bible-back-to-reading"
              className="text-sm text-[#7A756E] hover:text-[#2C2A29] transition-colors duration-300">
              Back to reading
            </button>
          </div>
          {searching ? (
            <div className="py-16 text-center"><Loader2 className="w-6 h-6 text-[#2C2A29] animate-spin mx-auto" /></div>
          ) : results.results.length === 0 ? (
            <p className="font-decree text-xl text-[#4A4744]" data-testid="bible-no-results">No verses found. Try another search.</p>
          ) : (
            <ul className="space-y-4">
              {results.results.map((r, i) => (
                <li key={i} data-testid={`bible-result-${i}`}>
                  <button onClick={() => openVerse(r)} className="w-full text-left group">
                    <span className="flex items-center gap-1.5 font-body text-sm text-[#9B7E4E] mb-1">
                      {r.book_name} {r.chapter}:{r.verse}
                      <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </span>
                    <p className="font-decree text-lg text-[#2C2A29] leading-relaxed">{r.text}</p>
                  </button>
                  <button
                    data-testid={`decree-verse-result-${i}`}
                    onClick={() => decreeVerse(`${r.book_name} ${r.chapter}:${r.verse}`, r.text)}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-wide text-[#9B7E4E] hover:text-[#2C2A29] transition-colors duration-300"
                  >
                    <Gavel className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Decree this verse
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-10" data-testid="bible-reading">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] tracking-tight">
            {bookName} {chapter}
          </h2>
          {verses === null ? (
            <div className="py-16 text-center"><Loader2 className="w-6 h-6 text-[#2C2A29] animate-spin mx-auto" /></div>
          ) : (
            <div className="mt-6 space-y-3">
              {verses.map((v) => {
                const ref = `${book}:${chapter}:${v.verse}`;
                const hl = highlights[ref];
                return (
                <p
                  key={v.verse}
                  data-testid={`verse-${v.verse}`}
                  style={hl ? { backgroundColor: HL_MAP[hl] } : undefined}
                  className={`group relative font-decree text-lg text-[#2C2A29] leading-relaxed rounded-sm transition-colors duration-300 ${hl ? "px-3 py-1.5 -mx-1" : ""}`}
                >
                  <sup className="font-body text-xs text-[#9B7E4E] mr-1.5 font-semibold">{v.verse}</sup>
                  {v.text}
                  {user && (
                  <button
                    data-testid={`highlight-verse-${v.verse}`}
                    onClick={() => setOpenHl(openHl === v.verse ? null : v.verse)}
                    title="Highlight verse"
                    aria-label={`Highlight verse ${v.verse}`}
                    className="ml-2 align-middle inline-flex items-center text-[#9B7E4E] hover:text-[#2C2A29] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <Highlighter className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                  )}
                  <button
                    data-testid={`decree-verse-${v.verse}`}
                    onClick={() => decreeVerse(`${bookName} ${chapter}:${v.verse}`, v.text)}
                    title="Decree this verse"
                    aria-label={`Decree ${bookName} ${chapter}:${v.verse}`}
                    className="ml-1.5 align-middle inline-flex items-center gap-1 text-xs uppercase tracking-wide text-[#9B7E4E] hover:text-[#2C2A29] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <Gavel className="w-3.5 h-3.5" strokeWidth={1.75} />
                    Decree
                  </button>
                  {openHl === v.verse && (
                    <span data-testid={`highlight-menu-${v.verse}`} className="ml-2 inline-flex items-center gap-1.5 align-middle bg-[#FDFBF7] border border-[#D9D4CC] rounded-full px-2 py-1">
                      {HL_COLORS.map((c) => {
                        const locked = user?.tier !== "premium" && c.key !== "yellow";
                        return (
                        <button
                          key={c.key}
                          data-testid={`highlight-color-${v.verse}-${c.key}`}
                          onClick={() => applyHighlight(v.verse, c.key)}
                          style={{ backgroundColor: c.bg }}
                          aria-label={`Highlight ${c.key}${locked ? " (Premium)" : ""}`}
                          className="relative w-5 h-5 rounded-full border border-[#00000018] hover:scale-110 transition-transform duration-200 flex items-center justify-center"
                        >
                          {locked && <Lock className="w-2.5 h-2.5 text-[#00000066]" strokeWidth={2.5} />}
                        </button>
                        );
                      })}
                      {hl && (
                        <button
                          data-testid={`highlight-remove-${v.verse}`}
                          onClick={() => applyHighlight(v.verse, null)}
                          aria-label="Remove highlight"
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[#7A756E] hover:text-[#2C2A29]"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  )}
                </p>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
